import { sourceLabelFromResult } from './resultFormatters.js';
import { csvEscape, triggerDownload } from './downloadUtils.js';

function maltegoEntityTypeFromValue(value, fallback = 'maltego.Phrase') {
  const v = String(value || '').trim();
  if (!v) return fallback;
  if (/^https?:\/\//i.test(v)) return 'maltego.URL';
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'maltego.EmailAddress';
  const digits = v.replace(/\D/g, '');
  if ((v.startsWith('+') || /^\d+$/.test(v)) && digits.length >= 8) return 'maltego.PhoneNumber';
  if (/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(v)) return 'maltego.Domain';
  if (/^[a-z]+(?:\s+[a-z]+){1,3}$/i.test(v)) return 'maltego.Person';
  return fallback;
}

function urlToDomain(urlValue) {
  try {
    return new URL(String(urlValue || '')).hostname || '';
  } catch {
    return '';
  }
}

/**
 * Export local graph files tuned for Maltego Community "Import from Table".
 * Produces entities + links CSV with explicit Maltego entity types.
 */
export function exportGraphForMaltego({ target = '', results = [], extractedLeads, moduleName = 'intelligence' }) {
  const entities = [];
  const links = [];
  const idByKey = new Map();
  const keyOf = (type, value) => `${type}|${String(value || '').trim().toLowerCase()}`;
  const addEntity = (type, value, label = '', extras = {}) => {
    const clean = String(value || '').trim();
    if (!clean) return '';
    const key = keyOf(type, clean);
    if (idByKey.has(key)) return idByKey.get(key);
    const id = `e${idByKey.size + 1}`;
    idByKey.set(key, id);
    entities.push({
      id,
      entity_type: type,
      value: clean,
      label: label || clean,
      ...extras,
    });
    return id;
  };
  const addLink = (sourceId, targetId, relationship) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    const fp = `${sourceId}|${targetId}|${relationship}`;
    if (links.some((l) => `${l.source_id}|${l.target_id}|${l.relationship}` === fp)) return;
    links.push({ source_id: sourceId, target_id: targetId, relationship });
  };

  const targetType = maltegoEntityTypeFromValue(target, 'maltego.PhoneNumber');
  const targetId = addEntity(targetType, target || 'unknown-target', 'Investigation target', { role: 'target' });

  for (const r of Array.isArray(results) ? results : []) {
    if (!r || typeof r !== 'object') continue;
    const srcLabel = sourceLabelFromResult(r);
    const srcId = addEntity('maltego.Phrase', srcLabel, srcLabel, {
      source: r.source || '',
      row_type: r.type || '',
      risk_score: r.risk_score ?? r.details?.risk_score ?? '',
      score: r.score ?? '',
    });
    addLink(targetId, srcId, 'observed_in_result');

    const title = String(r.title || '').trim();
    if (title) {
      const titleType = maltegoEntityTypeFromValue(title, 'maltego.Phrase');
      const titleId = addEntity(titleType, title, title);
      addLink(srcId, titleId, 'has_title');
    }
    const url = String(r.url || '').trim();
    if (url && /^https?:\/\//i.test(url)) {
      const urlId = addEntity('maltego.URL', url, url);
      addLink(srcId, urlId, 'references_url');
      const domain = urlToDomain(url);
      if (domain) {
        const domainId = addEntity('maltego.Domain', domain, domain);
        addLink(urlId, domainId, 'hosted_on');
      }
    }
    const owner = String(r.details?.owner_name || '').trim();
    if (owner) {
      const ownerId = addEntity('maltego.Person', owner, owner);
      addLink(targetId, ownerId, 'identity_candidate');
    }
  }

  const leadEmails = Array.isArray(extractedLeads?.emails) ? extractedLeads.emails : [];
  const leadUrls = Array.isArray(extractedLeads?.associated_urls) ? extractedLeads.associated_urls : [];
  for (const email of leadEmails) {
    const e = String(email || '').trim();
    if (!e) continue;
    const emailId = addEntity('maltego.EmailAddress', e, e);
    addLink(targetId, emailId, 'discovered_email');
  }
  for (const url of leadUrls) {
    const u = String(url || '').trim();
    if (!u || !/^https?:\/\//i.test(u)) continue;
    const urlId = addEntity('maltego.URL', u, u);
    addLink(targetId, urlId, 'discovered_url');
    const domain = urlToDomain(u);
    if (domain) {
      const domainId = addEntity('maltego.Domain', domain, domain);
      addLink(urlId, domainId, 'hosted_on');
    }
  }

  const entityCsv = [
    'id,entity_type,value,label,role,source,row_type,risk_score,score',
    ...entities.map((e) =>
      [
        csvEscape(e.id),
        csvEscape(e.entity_type),
        csvEscape(e.value),
        csvEscape(e.label),
        csvEscape(e.role || ''),
        csvEscape(e.source || ''),
        csvEscape(e.row_type || ''),
        csvEscape(e.risk_score ?? ''),
        csvEscape(e.score ?? ''),
      ].join(',')
    ),
  ].join('\n');

  const linksCsv = [
    'source_id,target_id,relationship',
    ...links.map((l) => [csvEscape(l.source_id), csvEscape(l.target_id), csvEscape(l.relationship)].join(',')),
  ].join('\n');

  const safeModule = String(moduleName).replace(/[^a-z0-9-_]+/gi, '-').slice(0, 40) || 'intelligence';
  const ts = Date.now();
  triggerDownload(`maltego-ce-${safeModule}-${ts}-entities.csv`, entityCsv, 'text/csv;charset=utf-8');
  triggerDownload(`maltego-ce-${safeModule}-${ts}-links.csv`, linksCsv, 'text/csv;charset=utf-8');
  triggerDownload(
    `maltego-ce-${safeModule}-${ts}.json`,
    JSON.stringify({ target, moduleName, entities, links }, null, 2),
    'application/json;charset=utf-8'
  );
}
